import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { settingsService } from '../services/settingsService';
import { useSettings } from './SettingsContext';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language translations
const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      connections: 'Connections',
      pipelines: 'Pipelines',
      create: 'Create Pipeline',
      explore: 'Explore',
      monitor: 'Monitor',
      schedule: 'Schedule',
      kafka: 'Kafka',
      sql: 'SQL Operations',
      settings: 'Settings',
      documentation: 'Documentation'
    },
    
    // Common terms
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      update: 'Update',
      loading: 'Loading...',
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
      confirm: 'Confirm',
      yes: 'Yes',
      no: 'No',
      reset: 'Reset',
      apply: 'Apply',
      close: 'Close',
      search: 'Search',
      filter: 'Filter',
      refresh: 'Refresh',
      export: 'Export',
      import: 'Import'
    },
    
    // Settings
    settings: {
      title: 'Settings',
      general: 'General',
      profile: 'Profile',
      pipelines: 'Pipelines',
      connections: 'Connections',
      notifications: 'Notifications',
      kafka: 'Kafka',
      security: 'Security',
      integrations: 'Integrations',
      advanced: 'Advanced',
      theme: 'Theme',
      language: 'Language',
      timezone: 'Timezone',
      sessionTimeout: 'Session Timeout',
      autoSave: 'Auto-save',
      saveChanges: 'Save Changes',
      unsavedChanges: 'You have unsaved changes'
    },
    
    // Pipelines
    pipelines: {
      title: 'Pipelines',
      create: 'Create Pipeline',
      status: 'Status',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      source: 'Source',
      target: 'Target',
      transformation: 'Transformation'
    },
    
    // Authentication
    auth: {
      login: 'Login',
      logout: 'Logout',
      signup: 'Sign Up',
      email: 'Email',
      password: 'Password',
      forgotPassword: 'Forgot Password?',
      rememberMe: 'Remember me',
      sessionExpiring: 'Session Expiring Soon',
      stayLoggedIn: 'Stay Logged In',
      logOutNow: 'Log Out Now'
    },
    
    // Time formatting
    time: {
      seconds: 'seconds',
      minutes: 'minutes',
      hours: 'hours',
      days: 'days',
      ago: 'ago',
      now: 'just now'
    }
  },
  
  es: {
    nav: {
      home: 'Inicio',
      connections: 'Conexiones',
      pipelines: 'Pipelines',
      create: 'Crear Pipeline',
      explore: 'Explorar',
      monitor: 'Monitorear',
      schedule: 'Programar',
      kafka: 'Kafka',
      sql: 'Operaciones SQL',
      settings: 'Configuración',
      documentation: 'Documentación'
    },
    
    common: {
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      create: 'Crear',
      update: 'Actualizar',
      loading: 'Cargando...',
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      info: 'Información',
      confirm: 'Confirmar',
      yes: 'Sí',
      no: 'No',
      reset: 'Restablecer',
      apply: 'Aplicar',
      close: 'Cerrar',
      search: 'Buscar',
      filter: 'Filtrar',
      refresh: 'Actualizar',
      export: 'Exportar',
      import: 'Importar'
    },
    
    settings: {
      title: 'Configuración',
      general: 'General',
      profile: 'Perfil',
      pipelines: 'Pipelines',
      connections: 'Conexiones',
      notifications: 'Notificaciones',
      kafka: 'Kafka',
      security: 'Seguridad',
      integrations: 'Integraciones',
      advanced: 'Avanzado',
      theme: 'Tema',
      language: 'Idioma',
      timezone: 'Zona horaria',
      sessionTimeout: 'Tiempo de sesión',
      autoSave: 'Guardado automático',
      saveChanges: 'Guardar cambios',
      unsavedChanges: 'Tienes cambios sin guardar'
    },
    
    pipelines: {
      title: 'Pipelines',
      create: 'Crear Pipeline',
      status: 'Estado',
      running: 'Ejecutando',
      completed: 'Completado',
      failed: 'Fallido',
      pending: 'Pendiente',
      source: 'Origen',
      target: 'Destino',
      transformation: 'Transformación'
    },
    
    auth: {
      login: 'Iniciar sesión',
      logout: 'Cerrar sesión',
      signup: 'Registrarse',
      email: 'Correo electrónico',
      password: 'Contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      rememberMe: 'Recordarme',
      sessionExpiring: 'Sesión expirando pronto',
      stayLoggedIn: 'Mantener sesión activa',
      logOutNow: 'Cerrar sesión ahora'
    },
    
    time: {
      seconds: 'segundos',
      minutes: 'minutos',
      hours: 'horas',
      days: 'días',
      ago: 'hace',
      now: 'ahora mismo'
    }
  },
  
  fr: {
    nav: {
      home: 'Accueil',
      connections: 'Connexions',
      pipelines: 'Pipelines',
      create: 'Créer Pipeline',
      explore: 'Explorer',
      monitor: 'Surveiller',
      schedule: 'Programmer',
      kafka: 'Kafka',
      sql: 'Opérations SQL',
      settings: 'Paramètres',
      documentation: 'Documentation'
    },
    
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      update: 'Mettre à jour',
      loading: 'Chargement...',
      success: 'Succès',
      error: 'Erreur',
      warning: 'Avertissement',
      info: 'Information',
      confirm: 'Confirmer',
      yes: 'Oui',
      no: 'Non',
      reset: 'Réinitialiser',
      apply: 'Appliquer',
      close: 'Fermer',
      search: 'Rechercher',
      filter: 'Filtrer',
      refresh: 'Actualiser',
      export: 'Exporter',
      import: 'Importer'
    },
    
    settings: {
      title: 'Paramètres',
      general: 'Général',
      profile: 'Profil',
      pipelines: 'Pipelines',
      connections: 'Connexions',
      notifications: 'Notifications',
      kafka: 'Kafka',
      security: 'Sécurité',
      integrations: 'Intégrations',
      advanced: 'Avancé',
      theme: 'Thème',
      language: 'Langue',
      timezone: 'Fuseau horaire',
      sessionTimeout: 'Délai de session',
      autoSave: 'Sauvegarde automatique',
      saveChanges: 'Enregistrer les modifications',
      unsavedChanges: 'Vous avez des modifications non enregistrées'
    },
    
    pipelines: {
      title: 'Pipelines',
      create: 'Créer Pipeline',
      status: 'Statut',
      running: 'En cours',
      completed: 'Terminé',
      failed: 'Échoué',
      pending: 'En attente',
      source: 'Source',
      target: 'Cible',
      transformation: 'Transformation'
    },
    
    auth: {
      login: 'Se connecter',
      logout: 'Se déconnecter',
      signup: "S'inscrire",
      email: 'Email',
      password: 'Mot de passe',
      forgotPassword: 'Mot de passe oublié?',
      rememberMe: 'Se souvenir de moi',
      sessionExpiring: 'Session bientôt expirée',
      stayLoggedIn: 'Rester connecté',
      logOutNow: 'Se déconnecter maintenant'
    },
    
    time: {
      seconds: 'secondes',
      minutes: 'minutes',
      hours: 'heures',
      days: 'jours',
      ago: 'il y a',
      now: "à l'instant"
    }
  },
  
  de: {
    nav: {
      home: 'Startseite',
      connections: 'Verbindungen',
      pipelines: 'Pipelines',
      create: 'Pipeline erstellen',
      explore: 'Erkunden',
      monitor: 'Überwachen',
      schedule: 'Planen',
      kafka: 'Kafka',
      sql: 'SQL-Operationen',
      settings: 'Einstellungen',
      documentation: 'Dokumentation'
    },
    
    common: {
      save: 'Speichern',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      create: 'Erstellen',
      update: 'Aktualisieren',
      loading: 'Wird geladen...',
      success: 'Erfolg',
      error: 'Fehler',
      warning: 'Warnung',
      info: 'Information',
      confirm: 'Bestätigen',
      yes: 'Ja',
      no: 'Nein',
      reset: 'Zurücksetzen',
      apply: 'Anwenden',
      close: 'Schließen',
      search: 'Suchen',
      filter: 'Filtern',
      refresh: 'Aktualisieren',
      export: 'Exportieren',
      import: 'Importieren'
    },
    
    settings: {
      title: 'Einstellungen',
      general: 'Allgemein',
      profile: 'Profil',
      pipelines: 'Pipelines',
      connections: 'Verbindungen',
      notifications: 'Benachrichtigungen',
      kafka: 'Kafka',
      security: 'Sicherheit',
      integrations: 'Integrationen',
      advanced: 'Erweitert',
      theme: 'Design',
      language: 'Sprache',
      timezone: 'Zeitzone',
      sessionTimeout: 'Sitzungstimeout',
      autoSave: 'Automatisches Speichern',
      saveChanges: 'Änderungen speichern',
      unsavedChanges: 'Sie haben ungespeicherte Änderungen'
    },
    
    pipelines: {
      title: 'Pipelines',
      create: 'Pipeline erstellen',
      status: 'Status',
      running: 'Läuft',
      completed: 'Abgeschlossen',
      failed: 'Fehlgeschlagen',
      pending: 'Ausstehend',
      source: 'Quelle',
      target: 'Ziel',
      transformation: 'Transformation'
    },
    
    auth: {
      login: 'Anmelden',
      logout: 'Abmelden',
      signup: 'Registrieren',
      email: 'E-Mail',
      password: 'Passwort',
      forgotPassword: 'Passwort vergessen?',
      rememberMe: 'Angemeldet bleiben',
      sessionExpiring: 'Sitzung läuft bald ab',
      stayLoggedIn: 'Angemeldet bleiben',
      logOutNow: 'Jetzt abmelden'
    },
    
    time: {
      seconds: 'Sekunden',
      minutes: 'Minuten',
      hours: 'Stunden',
      days: 'Tage',
      ago: 'vor',
      now: 'gerade eben'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const { languageSettings, isLoading: settingsLoading } = useSettings();
  const [language, setLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);

  // Load language from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && languageSettings) {
      if (languageSettings.language) {
        setLanguage(languageSettings.language);
      }
      setIsLoading(false);
    }
  }, [languageSettings, settingsLoading]);

  const loadLanguageFromSettings = async () => {
    // Only used for revert on error
    try {
      const response = await settingsService.getEffectiveSettings();
      if (response.success && response.data) {
        const generalSettings = response.data.find(cat => cat.category === 'GENERAL');
        if (generalSettings && generalSettings.settings.language) {
          setLanguage(generalSettings.settings.language);
        }
      }
    } catch (error) {
      logger.warn('Could not load language from settings, using default:', error);
    }
  };

  const updateLanguage = async (newLanguage) => {
    try {
      setLanguage(newLanguage);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        language: newLanguage
      });
    } catch (error) {
      logger.error('Failed to save language setting:', error);
      // Revert language on error
      await loadLanguageFromSettings();
    }
  };

  // Translation function
  const t = (key, defaultText = key) => {
    const keys = key.split('.');
    let translation = translations[language];
    
    for (const k of keys) {
      if (translation && translation[k]) {
        translation = translation[k];
      } else {
        // Fallback to English if translation not found
        translation = translations.en;
        for (const fallbackKey of keys) {
          if (translation && translation[fallbackKey]) {
            translation = translation[fallbackKey];
          } else {
            return defaultText;
          }
        }
        break;
      }
    }
    
    return typeof translation === 'string' ? translation : defaultText;
  };

  // Pluralization function
  const plural = (count, singular, plural) => {
    if (language === 'en') {
      return count === 1 ? singular : plural;
    }
    // Add other language pluralization rules as needed
    return count === 1 ? singular : plural;
  };

  // Format number according to locale
  const formatNumber = (number) => {
    const locale = getLocaleFromLanguage(language);
    return new Intl.NumberFormat(locale).format(number);
  };

  // Format date according to locale
  const formatDate = (date, options = {}) => {
    const locale = getLocaleFromLanguage(language);
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  };

  // Format currency according to locale
  const formatCurrency = (amount, currency = 'USD') => {
    const locale = getLocaleFromLanguage(language);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const value = {
    language,
    setLanguage: updateLanguage,
    isLoading,
    t,
    plural,
    formatNumber,
    formatDate,
    formatCurrency,
    availableLanguages: Object.keys(translations)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Helper function to get locale from language code
const getLocaleFromLanguage = (language) => {
  const localeMap = {
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    pt: 'pt-PT',
    zh: 'zh-CN',
    ja: 'ja-JP'
  };
  
  return localeMap[language] || 'en-US';
};

export default LanguageContext;